<script setup lang="ts">
import { Product } from '@/types/api/api-v3/product'
import { ProductParentType } from '@/types/components/product-card.types'
import { NAME_PARENT_COMPONENT, TagSize } from '@/types/products'

import ProductOldTag from '@/components/Product/ProductItem/ProductTags/ProductTag.vue'
import ProductTagRating from '@/components/Product/ProductTags/ProductTagRating.vue'

type Props = {
  product: Product
  parentComponent: string
}

const props = defineProps<Props>()

const tagsClasses = computed(() => [
  'product__tags',
  {
    'product__tags--wide':
      NAME_PARENT_COMPONENT.MAIN_DESKTOP === props.parentComponent &&
      props.product.tags.length > 5,
  },
])

const tagSize = computed(() =>
  NAME_PARENT_COMPONENT.DIALOG === props.parentComponent
    ? TagSize.LARGE
    : TagSize.NORMAL,
)
</script>

<template>
  <div :class="tagsClasses">
    <product-tag-rating :product="product" :type="ProductParentType.detail" />

    <product-old-tag
      v-for="tag in product.tags"
      :key="tag.name"
      :tag="tag"
      :size="tagSize"
    />
  </div>
</template>

<style scoped lang="scss">
@mixin product-main-desktop {
  .product__tags {
    position: absolute;
    top: 0;
    left: 0;
    display: grid;
    gap: 10px 0;
    margin-top: 12px;

    &--wide {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      width: 54px;
      max-height: 150px;
    }
  }
}

@mixin product-main-mobile {
  .product__tags {
    position: absolute;
    top: 12px;
    left: 12px;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 100px;

    @include media('>=tablet') {
      gap: 10px;
    }

    @include media('<=desktop-mini') {
      max-height: 160px;
    }

    @include media('<tablet') {
      max-height: 110px;
    }
  }
}

@mixin product-dialog {
  .product__tags {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 16px;
    height: calc(100% - 34px);

    @include media('<=desktop-mini') {
      left: 48px;
    }

    @include media('<tablet') {
      left: 20px;
    }
  }
}

@mixin product-page {
  .product__tags {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 5;
    display: grid;
    gap: 12px 0;
    padding: 25px 0 0 5px;

    @include media('<=tablet') {
      padding: 0;
    }
  }
}

.product--main-desktop {
  @include product-main-desktop();
}

.product--main-mobile {
  @include product-main-mobile;
}

.product--dialog {
  @include product-dialog;
}

.product--page {
  @include product-page;
}
</style>
